import React from 'react';
import { Select } from 'formik-antd';
import { Row, Col } from 'antd';
import * as Yup from 'yup';

import {
  BankingInfoAccountTypeEnum,
  BOUsersBankingInfoTypeEnum,
} from 'api/backofficeApiClient';
import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import Input from 'components/formik/Input';
import { getBankingAccountTypesOptions } from 'utils/accountUtils';
import { maxTextInputCharactersLength } from 'utils/validationUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './DirectDeposit.module.less';

export const isDirectDepositTypeSelected = (
  bankingInfoType: BOUsersBankingInfoTypeEnum,
) => bankingInfoType === BOUsersBankingInfoTypeEnum.DirectDeposit;

export interface DirectDepositFormValues {
  bankName: string;
  holderName: string;
  accountNumber: string;
  accountNumberConfirmation: string;
  routingNumber: string;
  routingNumberConfirmation: string;
  accountType: BankingInfoAccountTypeEnum | null;
  ddEmailAddress: string;
  directDepositConfirmation: boolean;
}

export const directDepositValidationSchema = Yup.object({
  bankName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `Bank name should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  holderName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `Bank account holder name should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  accountNumber: Yup.string()
    .trim()
    .required('Field is required')
    .max(250, 'Account number should be 250 characters maximum'),
  accountNumberConfirmation: Yup.string()
    .trim()
    .required('Field is required')
    .oneOf(
      [Yup.ref('accountNumber')],
      'Account confirmation number should be the same as account number',
    ),
  routingNumber: Yup.string()
    .trim()
    .required('Field is required')
    .max(250, 'Routing number should be 250 characters maximum'),
  routingNumberConfirmation: Yup.string()
    .required('Field is required')
    .oneOf(
      [Yup.ref('routingNumber')],
      'Routing confirmation number should be the same as routing number',
    ),
  accountType: Yup.number().nullable().required('Field is required'),
  ddEmailAddress: Yup.string()
    .trim()
    .email('Email format is invalid')
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `Direct deposit email address should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  directDepositConfirmation: Yup.boolean().oneOf(
    [true],
    'Please authorize myBasePay, LLC to initiate credit/debit entries in to the account listed above in order to continue',
  ),
});

export default function DirectDeposit() {
  const isAdmin = useAppSelector(isAdminSelector);

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <FormItem
            name="directDepositDetail.routingNumber"
            label="Routing number *"
          >
            <Input
              name="directDepositDetail.routingNumber"
              placeholder="Enter routing number"
              secureMode={!isAdmin}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="directDepositDetail.routingNumberConfirmation"
            label="Confirm routing number *"
          >
            <Input
              name="directDepositDetail.routingNumberConfirmation"
              placeholder="Confirm routing number"
              secureMode={!isAdmin}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="directDepositDetail.accountNumber"
            label="Account number *"
          >
            <Input
              name="directDepositDetail.accountNumber"
              placeholder="Enter account number"
              secureMode={!isAdmin}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="directDepositDetail.accountNumberConfirmation"
            label="Confirm account number *"
          >
            <Input
              name="directDepositDetail.accountNumberConfirmation"
              placeholder="Confirm account number"
              secureMode={!isAdmin}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="directDepositDetail.holderName"
            label="Bank account holder name *"
          >
            <Input
              name="directDepositDetail.holderName"
              placeholder="Enter bank account holder name"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem name="directDepositDetail.bankName" label="Bank name *">
            <Input
              name="directDepositDetail.bankName"
              placeholder="Enter bank name"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="directDepositDetail.accountType"
            label="Account type *"
          >
            <Select
              name="directDepositDetail.accountType"
              placeholder="Select account type"
            >
              {getBankingAccountTypesOptions().map((accountType) => (
                <Select.Option
                  key={accountType.value}
                  value={accountType.value}
                >
                  {accountType.label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="directDepositDetail.ddEmailAddress"
            label="Direct deposit email address *"
          >
            <Input
              name="directDepositDetail.ddEmailAddress"
              placeholder="Enter DD email address"
            />
          </FormItem>

          <div className={styles.note}>
            Please Note: all direct deposit notifications will come directly
            from accountspayable@mybasepay.com. Please add this email to your
            Contacts list to avoid this being sent to Spam
          </div>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col xs={24}>
          <FormItem
            name="directDepositConfirmation"
            className="directDepositDetail checkbox-wrapper"
          >
            <Checkbox name="directDepositDetail.directDepositConfirmation">
              I hereby authorize myBasePay, LLC to initiate credit/debit entries
              in to the account listed above. I understand that it's my
              responsibility to make any changes to my bank account information
              with myBasePay. I will not hold myBasePay liable for any delay in
              payment
            </Checkbox>
          </FormItem>
        </Col>
      </Row>
    </>
  );
}
